import { useFeatureIsOn } from '@growthbook/growthbook-react';
import { Station } from '@mi-front/core';
import { LinkItem } from '@mi-front/ui';
import { useIntl } from 'react-intl';
import { isVideoStation } from '../../../../../core/stations/helpers';
import { useIsContentAiForStationEnabled } from '../../../../content-ai/hooks/useIsContentAiForStationEnabled';
import { Fragment } from '../../../types';

/**
 * Returns the tab items for the fragment view header (Info, Archivos, Edición, Publicar, etc.).
 * Tabs are filtered by station type and feature flags (e.g. video edition, Content AI).
 */
export function useFragmentTabs(station: Station, fragment: Fragment): LinkItem[] {
  const { $t } = useIntl();

  const isVideoEditionEnabled = useFeatureIsOn('app__fragments_video_edition_enabled');
  const isContentAiEnabled = useIsContentAiForStationEnabled(station.id);

  const isMediaReady = fragment.media.status === 'READY';
  const isTranscriptionReady = !!fragment.transcription?.completedAt;

  const tabs: (LinkItem | null)[] = [
    {
      exact: true,
      to: `/fragments/${fragment.id}`,
      text: $t({
        id: 'fragments.viewScreen.title',
        defaultMessage: 'Info',
      }),
      isActive: (_match, location) => {
        return (
          location.pathname === `/fragments/${fragment.id}` ||
          location.pathname === `/fragments/s/${station.slug}/fragment/${fragment.id}` ||
          location.pathname === `/fragments/s/${station.id}/fragment/${fragment.id}`
        );
      },
    },
    {
      to: `/fragments/${fragment.id}/files`,
      text: $t({
        id: 'fragments.exportView.tabFiles',
        defaultMessage: 'Archivos',
      }),
      isActive: (_match, location) => location.pathname === `/fragments/${fragment.id}/files`,
      disabled: !isMediaReady,
    },
    isVideoStation(station) && isVideoEditionEnabled
      ? {
          to: `/fragments/${fragment.id}/video-editions`,
          text: $t({
            id: 'fragments.videoEditions.tabTitle',
            defaultMessage: 'Edición',
          }),
          disabled: !isMediaReady || !isTranscriptionReady,
        }
      : null,
    {
      to: `/fragments/${fragment.id}/publish`,
      text: $t({
        id: 'fragments.exportView.tabPublish',
        defaultMessage: 'Publicar',
      }),
      isActive: (_match, location) => location.pathname === `/fragments/${fragment.id}/publish`,
      disabled: !isMediaReady,
    },
    {
      to: `/fragments/${fragment.id}/audiogram`,
      text: $t({
        id: 'fragments.audiogram.title',
        defaultMessage: 'Audiogram',
      }),
      disabled: !isMediaReady || !isTranscriptionReady,
    },
    isContentAiEnabled
      ? ({
          to: `/fragments/${fragment.id}/content-ai`,
          text: $t({
            id: 'contentAi.title',
            defaultMessage: 'Content AI',
          }),
          disabled: !isMediaReady || !isTranscriptionReady,
        } as LinkItem)
      : null,
  ];

  return tabs.filter((v): v is LinkItem => Boolean(v));
}
